import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, TrendingDown as DepreciateIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as fixedAssetService from '../services/fixedAssetService';

const FixedAssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useSelector((state) => state.auth);
  const company = user?.company;

  const currency = company?.currency || 'USD';
  const decimals = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
  
  const [depreciateOpen, setDepreciateOpen] = useState(false);
  const [depreciateForm, setDepreciateForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [depreciateLoading, setDepreciateLoading] = useState(false);

  useEffect(() => {
    fetchAsset();
  }, [id]);

  const fetchAsset = async () => {
    try {
      setLoading(true);
      const res = await fixedAssetService.getAssetById(id);
      if (res.success) {
        setAsset(res.data);
      }
    } catch (error) {
      console.error('Failed to load asset', error);
      alert('Failed to load asset details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    const amount = Number(val || 0);
    try {
      return new Intl.NumberFormat(undefined, { 
        style: 'currency', 
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(decimals)}`;
    }
  };

  const handleDepreciateSubmit = async (e) => {
    e.preventDefault();
    try {
      setDepreciateLoading(true);
      const res = await fixedAssetService.runDepreciation(id, depreciateForm);
      if (res.success) {
        setDepreciateOpen(false);
        setDepreciateForm({ amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
        fetchAsset();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to apply depreciation');
    } finally {
      setDepreciateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!asset) {
    return <Typography>Asset not found</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fixed-assets')}>
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {asset.assetCode} - {asset.name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DepreciateIcon />}
          color="secondary"
          onClick={() => setDepreciateOpen(true)}
          disabled={asset.status !== 'Active' || parseFloat(asset.currentBookValue) <= parseFloat(asset.salvageValue)}
        >
          Run Depreciation
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom>Asset Details</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip
                label={asset.status}
                color={
                  asset.status === 'Active' ? 'success' :
                  asset.status === 'Disposed' ? 'error' :
                  asset.status === 'Sold' ? 'info' : 'warning'
                }
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Category</Typography>
              <Typography variant="body1">{asset.category?.name || 'N/A'}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Purchase Date</Typography>
              <Typography variant="body1">{new Date(asset.purchaseDate).toLocaleDateString()}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Location</Typography>
              <Typography variant="body1">{asset.location || 'N/A'}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Serial Number</Typography>
              <Typography variant="body1">{asset.serialNumber || 'N/A'}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Depreciation Method</Typography>
              <Typography variant="body1">{asset.depreciationMethod} ({asset.depreciationRate}%)</Typography>
            </Box>

          </Paper>
        </Grid>
        
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom>Value Overview</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Purchase Cost</Typography>
                <Typography variant="h5" color="primary">{formatCurrency(asset.purchaseCost)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Current Book Value</Typography>
                <Typography variant="h5" color="success.main">{formatCurrency(asset.currentBookValue)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Salvage Value</Typography>
                <Typography variant="h5" color="text.secondary">{formatCurrency(asset.salvageValue)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom>Depreciation History</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Previous Value</TableCell>
                    <TableCell>Depreciation Amount</TableCell>
                    <TableCell>New Book Value</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asset.depreciations && asset.depreciations.map((dep) => (
                    <TableRow key={dep.id}>
                      <TableCell>{new Date(dep.date).toLocaleDateString()}</TableCell>
                      <TableCell>{formatCurrency(dep.previousBookValue)}</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>-{formatCurrency(dep.amount)}</TableCell>
                      <TableCell>{formatCurrency(dep.newBookValue)}</TableCell>
                      <TableCell>{dep.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!asset.depreciations || asset.depreciations.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No depreciation history found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Depreciate Dialog */}
      <Dialog open={depreciateOpen} onClose={() => setDepreciateOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleDepreciateSubmit}>
          <DialogTitle>Run Depreciation</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" gutterBottom>
                  Current Book Value: <strong>{formatCurrency(asset.currentBookValue)}</strong><br />
                  Salvage Value: <strong>{formatCurrency(asset.salvageValue)}</strong>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Depreciation Amount *"
                  type="number"
                  inputProps={{ min: 0.01, step: "0.01", max: asset.currentBookValue - asset.salvageValue }}
                  value={depreciateForm.amount}
                  onChange={(e) => setDepreciateForm({ ...depreciateForm, amount: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Date *"
                  type="date"
                  value={depreciateForm.date}
                  onChange={(e) => setDepreciateForm({ ...depreciateForm, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={depreciateForm.notes}
                  onChange={(e) => setDepreciateForm({ ...depreciateForm, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDepreciateOpen(false)} disabled={depreciateLoading}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary" disabled={depreciateLoading}>
              {depreciateLoading ? 'Processing...' : 'Apply Depreciation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default FixedAssetDetail;
